/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T19:25:04+05:00
Intent: userName$ no longer replays the last saved name, so one account's renamed owner doesn't appear in the next account's sidebar.
*/
import {
  HttpClient,
  HttpEvent,
  HttpRequest,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { SecureStorageService } from './secure-storage.service';
import { MicroApiService } from '../constants/micro-api.service';
import { formatPlanLimitMessage } from '../utils/plan-limit-message.util';

// app-alert.model.ts
export interface AppAlert {
  id: number;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
  action?: any;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  showSuccess(message: string): void {
    this.createNotification('success', message);
  }
  // ✅ AFTER (Correct)
  private profileImageSource = new BehaviorSubject<string>(
    this.getInitialProfileImage() // localStorage se load karta hai
  );

  private getInitialProfileImage(): string {
    const storedImage = localStorage.getItem('profileImage');
    if (storedImage && storedImage !== 'assets/images/profile.png') {
      return storedImage;
    }
    return 'assets/images/profile.png';
  }
  profileImage$ = this.profileImageSource.asObservable();
  // Owner name after a profile save, so the sidebar "Hi, <name>" updates without a reload.
  // A Subject, not a BehaviorSubject: this service outlives a logout, and a replayed name
  // showed account A's new name in account B's sidebar after B signed in. A freshly built
  // sidebar reads the name from the signed-in user's details instead.
  private userNameSource = new Subject<string>();
  userName$ = this.userNameSource.asObservable();
  updateUserName(name: string) {
    this.userNameSource.next(name);
  }
  private planTypeSubject = new BehaviorSubject<'none' | 'basic' | 'advanced'>('none');
  constructor(
    private notification: NzNotificationService,
    private modal: NzModalService,
    private http: HttpClient,
    private secureStorage: SecureStorageService,
    // Resolved lazily (not injected directly) — TranslateService's HTTP loader goes through
    // HttpClient -> interceptors -> SharedService, so a direct constructor dependency here
    // creates a circular DI graph that breaks translation loading app-wide at bootstrap.
    private injector: Injector
  ) {
    const savedAlerts = localStorage.getItem('app_alerts');
    if (savedAlerts) {
      try {
        const parsed = JSON.parse(savedAlerts);
        this.alertsSubject.next(parsed);
      } catch (e) {
        console.error('Failed to parse saved alerts:', e);
      }
    }
  }

  public updatedUserData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private breadcrumbSubject = new BehaviorSubject<any[]>([]);
  breadcrumb$ = this.breadcrumbSubject.asObservable();
  checkHomePage = true;

  private contestSubject = new BehaviorSubject<any>(null); // Stores contest data
  contest$: Observable<any> = this.contestSubject.asObservable(); // Observable for subscribers

  // Function to set contest data
  setContest(data: any) {
    this.contestSubject.next(data);
  }

  // Function to get the latest contest data (can be used if needed)
  getContest(): Observable<any> {
    return this.contest$;
  }
  setBreadcrumb(breadcrumb: any[]) {
    this.breadcrumbSubject.next(breadcrumb);
  }

  closeAllModals(): void {
    this.modal.closeAll();
  }

  private lastNotificationMessage: string = '';
  private lastNotificationTime: number = 0;

  // Use this in catch blocks instead of createNotification — automatically ignores
  // EmptyError (thrown by firstValueFrom when interceptor returns EMPTY on session expiry)
  showError(error: any, fallbackMessage = 'An error occurred'): void {
    if (error?.name === 'EmptyError') return;
    // status 0 = the request never reached the server; the HttpErrorResponse's own message is
    // a raw "Http failure response …" string, so say what actually happened instead.
    if (error?.status === 0) {
      this.createNotification('error', 'We could not reach the server. Check your connection and try again.');
      return;
    }
    this.createNotification('error', error?.error?.message || fallbackMessage);
  }

  createNotification(type: string, message: string): void {
    if (!message) return;
    // Backend plan-limit errors are reworded ("campaigns", no "up to 0") and get a purple OK.
    const planLimit = formatPlanLimitMessage(message);
    message = planLimit.text;

    const now = Date.now();
    // Debounce exact same notification message within 2 seconds
    if (this.lastNotificationMessage === message && now - this.lastNotificationTime < 2000) {
      return;
    }
    this.lastNotificationMessage = message;
    this.lastNotificationTime = now;

    // Close any previous open modal before displaying a new notification
    this.modal.closeAll();

    const translate = this.injector.get(TranslateService);
    const normalizedType = type.toLowerCase();
    const modalClass = `contest-styled-modal contest-modal-${type}` +
      (planLimit.isPlanLimit ? ' contest-modal-plan-limit' : '');
    const titleKey = `common.notification-${normalizedType}`;

    const isAr = localStorage.getItem('lang') === 'ar' || translate?.currentLang === 'ar';
    let localizedMessage = message;
    if (message === 'paid') {
      localizedMessage = isAr ? 'تمت عملية الدفع بنجاح' : 'Payment successful!';
    } else if (isAr) {
      const arabicTranslations: Record<string, string> = {
        'Excel downloaded successfully': 'تم تنزيل ملف Excel بنجاح',
        'PDF downloaded successfully': 'تم تنزيل ملف PDF بنجاح',
        'CSV downloaded successfully': 'تم تنزيل ملف CSV بنجاح',
        'Invoice downloaded successfully': 'تم تنزيل الفاتورة بنجاح',
        'No discounts found': 'لم يتم العثور على خصومات',
        'Configuration PDF downloaded successfully': 'تم تنزيل ملف إعدادات PDF بنجاح',
        'Image saved successfully': 'تم حفظ الصورة بنجاح',
        'Copied to clipboard': 'تم النسخ إلى الحافظة',
        'Failed to copy': 'تعذّر النسخ',
        'Failed to load country data': 'تعذّر تحميل بيانات الدول',
        'An error occurred': 'حدث خطأ',
        'We could not reach the server. Check your connection and try again.': 'تعذّر الوصول إلى الخادم. تحقق من اتصالك وحاول مرة أخرى.',
        // Session/auth messages — sent by the backend in English, or raised by the interceptor.
        'Invalid or expired token': 'رمز الدخول غير صالح أو منتهي الصلاحية. يرجى تسجيل الدخول مرة أخرى.',
        'Invalid token': 'رمز الدخول غير صالح. يرجى تسجيل الدخول مرة أخرى.',
        'Token expired': 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.',
        'Token is expired': 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.',
        'jwt expired': 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.',
        'Unauthorized': 'غير مصرح لك بالوصول.',
        'Unauthorized access': 'غير مصرح لك بالوصول.',
        'Your session has expired. Please login again.': 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.',
        'Invalid authentication token. Please login again.': 'رمز المصادقة غير صالح. يرجى تسجيل الدخول مرة أخرى.',
        'Authentication failed. Please login again.': 'فشلت المصادقة. يرجى تسجيل الدخول مرة أخرى.',
        'Please complete your company setup': 'يرجى إكمال إعداد شركتك',
        'Please subscribe to a plan': 'يرجى الاشتراك في خطة',
        'Please verify your account using OTP': 'يرجى توثيق حسابك باستخدام رمز التحقق',
        'Access denied. Please contact support.': 'تم رفض الوصول. يرجى التواصل مع الدعم.'
      };
      // Backend wording varies in case and trailing full stop, so match loosely.
      const normalize = (s: string) => s.trim().replace(/\.+$/, '').toLowerCase();
      const wanted = normalize(message);
      const match = Object.keys(arabicTranslations).find(k => normalize(k) === wanted);
      if (match) {
        localizedMessage = arabicTranslations[match];
      }
    }

    // .get() (not .instant()) waits for the translation file to finish loading —
    // .instant() returns the raw key when called before the async HTTP load resolves
    // (e.g. an early session-expiry error on app bootstrap), which showed literal
    // strings like "common.notification-error" / "common.ok" instead of real text.
    translate.get([titleKey, 'common.ok', localizedMessage]).subscribe((translations) => {
      const title = translations[titleKey] || (isAr ? (normalizedType === 'success' ? 'نجاح' : normalizedType === 'error' ? 'خطأ' : 'تنبيه') : titleKey);
      const okText = translations['common.ok'] || (isAr ? 'موافق' : 'OK');
      const content = translations[localizedMessage] || localizedMessage;

      switch (normalizedType) {
        case 'success':
          this.modal.success({ nzTitle: title, nzContent: content, nzCentered: true, nzClassName: modalClass, nzOkText: okText });
          break;
        case 'error':
          this.modal.error({ nzTitle: title, nzContent: content, nzCentered: true, nzClassName: modalClass, nzOkText: okText });
          break;
        case 'warning':
          this.modal.warning({ nzTitle: title, nzContent: content, nzCentered: true, nzClassName: modalClass, nzOkText: okText });
          break;
        case 'info':
        default:
          this.modal.info({ nzTitle: title, nzContent: content, nzCentered: true, nzClassName: modalClass, nzOkText: okText });
          break;
      }
    });
  }

  getUserDetails() {
    return this.secureStorage.getItem('userDetails');
  }

  transformKeys(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(this.transformKeys.bind(this));
    } else if (obj !== null && obj.constructor === Object) {
      return Object.keys(obj).reduce((acc, key) => {
        const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[camelCaseKey] = this.transformKeys(obj[key]);
        return acc;
      }, {} as any);
    }
    return obj;
  }

  private platformSubject = new BehaviorSubject<string>('');
  currentPlatform$ = this.platformSubject.asObservable();

  setPlatform(platform: string): void {
    this.platformSubject.next(platform);
    localStorage.setItem('platform', platform);
  }
  updateProfileImage(imageUrl: string) {
    this.profileImageSource.next(imageUrl);

    // ✅ Automatically sync with localStorage
    if (imageUrl && imageUrl !== 'assets/images/profile.png') {
      localStorage.setItem('profileImage', imageUrl);
    } else {
      localStorage.removeItem('profileImage');
    }
  }

  getPlatform() {
    return localStorage.getItem('platform')?.toString();
  }

  private baseUrl = 'http://your-backend-url/upload'; // Replace with your backend URL

  upload(file: File): Observable<HttpEvent<any>> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    const headers = new HttpHeaders();
    // You can add custom headers if needed
    // headers = headers.append('Content-Type', 'multipart/form-data');

    return this.http.post<any>(this.baseUrl, formData, {
      headers: headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  // Copies text and notifies. Falls back to execCommand where the async clipboard API
  // isn't available (older browsers, non-secure contexts).
  copyToClipboard(text: string): void {
    if (!text) return;

    const onCopied = () => this.createNotification('success', 'Copied to clipboard');
    const onFailed = () => this.createNotification('error', 'Failed to copy');

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(onCopied)
        .catch(() => (this.legacyCopy(text) ? onCopied() : onFailed()));
      return;
    }

    this.legacyCopy(text) ? onCopied() : onFailed();
  }

  private legacyCopy(text: string): boolean {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    } catch {
      return false;
    }
  }

  baseToImage(dataUrl: string): Observable<any> {
    // Resolved lazily — see getSupportedCountries. Gateway action 2461: a base64 data URI in,
    // the hosted file object { url, path, name, type, access, mime } out.
    return this.injector.get(MicroApiService).route('BASE_TO_IMAGE', false, { url: dataUrl });
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Loader                                   */
  /* -------------------------------------------------------------------------- */
  private loaderSubject = new BehaviorSubject<boolean>(false);
  loaderState = this.loaderSubject.asObservable();

  showLoader() {
    this.loaderSubject.next(true);
  }

  hideLoader() {
    this.loaderSubject.next(false);
  }

  /* -------------------------------------------------------------------------- */
  /*                              Dashboard Mode                                */
  /* -------------------------------------------------------------------------- */
  // Track if we're on dashboard (with sidebar) or public page
  private isDashboardSubject = new BehaviorSubject<boolean>(false);
  isDashboard$ = this.isDashboardSubject.asObservable();

  setDashboardMode(value: boolean) {
    this.isDashboardSubject.next(value);
  }
  setCampaignLocal(value: any) {
    localStorage.setItem('campaignEdit', value);
  }
  getCampaignLocal() {
    const check = localStorage.getItem('campaignEdit');
    if (check && check == 'true') return true;
    return false;
  }

  timeRemaining(createdAt: string): string {
    if (!createdAt) return 'No date provided';

    const targetDate = new Date(createdAt);
    const now = new Date();

    const diff = targetDate.getTime() - now.getTime();

    if (isNaN(targetDate.getTime())) return 'Invalid date';
    if (diff <= 0) return 'Expired';

    const totalMinutes = Math.floor(diff / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);

    return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''
      } left`;
  }

  private countryRecordsCache$: Promise<any[]> | null = null;
  private supportedCountriesCache$: Promise<any[]> | null = null;

  /**
   * The countries a CONTEST may target.
   *
   * Separate from getCountryRecords() on purpose. That one returns the full ISO list and
   * feeds phone dial codes, where any country is legitimate. A contest Location becomes
   * contests.target_country_code — a foreign key to the platform's own countries table —
   * so offering the full list meant offering places a contest could not be saved for: pick
   * Afghanistan, fill in the whole form, and only Submit tells you.
   */
  getSupportedCountries(): Promise<any[]> {
    if (this.supportedCountriesCache$) {
      return this.supportedCountriesCache$;
    }
    // MicroApiService is resolved LAZILY, for the reason the constructor comment gives: a
    // direct dependency would run HttpClient -> interceptors -> SharedService and close the
    // DI graph on itself, which breaks translation loading app-wide at bootstrap.
    const micro = this.injector.get(MicroApiService);
    this.supportedCountriesCache$ = firstValueFrom(micro.call('GET_SUPPORTED_COUNTRIES' as any))
      .then((data: any) => {
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        return rows
          .map((country: any) => ({
            name: country.name || '',
            code: (country.code || '').toLowerCase(),
            dial_code: country.dial_code || '',
            phone_number_length: country.phone_number_length || 0,
          }))
          .filter((c: any) => c.name && c.code)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        this.supportedCountriesCache$ = null; // Clear on error so it can retry
        console.error('Error loading supported countries:', error);
        this.createNotification('error', 'Failed to load country data');
        return [];
      });
    return this.supportedCountriesCache$;
  }

  getCountryRecords(): Promise<any[]> {
    if (this.countryRecordsCache$) {
      return this.countryRecordsCache$;
    }
    // 2402 — the public country list. MicroApiService is resolved lazily (see above).
    const micro = this.injector.get(MicroApiService);
    this.countryRecordsCache$ = firstValueFrom(micro.call('GET_COUNTRIES'))
      .then((data: any) => {
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        return rows
          .map((country: any) => ({
            name: country.name || '',
            code: country.code?.toLowerCase() || '',
            dial_code: country.dial_code || '',
            phone_number_length: country.phone_number_length || 0
          }))
          .filter((c: any) => c.name && c.code)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        this.countryRecordsCache$ = null; // Clear on error so it can retry
        console.error('Error loading country data:', error);
        this.createNotification('error', 'Failed to load country data');
        return [];
      });
    return this.countryRecordsCache$;
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Alerts                                   */
  /* -------------------------------------------------------------------------- */

  private alertsKey = 'app_alerts';
  private alertsSubject = new BehaviorSubject<AppAlert[]>(this.loadAlerts());
  alerts$ = this.alertsSubject.asObservable();
  private alertIdCounter = 1;
  private tempHiddenAlerts = new Set<number>();
  private hiddenAlertIds = new Set<number>();

  private loadAlerts(): AppAlert[] {
    const stored = localStorage.getItem(this.alertsKey);
    const allAlerts: AppAlert[] = stored ? JSON.parse(stored) : [];

    // Only hide alerts currently in the in-memory set
    return allAlerts.filter((alert) => !this.hiddenAlertIds?.has(alert.id));
  }

  private saveAlerts(alerts: AppAlert[]) {
    localStorage.setItem(this.alertsKey, JSON.stringify(alerts));
  }

  private updateStorage(alerts: AppAlert[]) {
    localStorage.setItem('app_alerts', JSON.stringify(alerts));
  }

  showAlert(type: AppAlert['type'], message: string, action?: string) {
    const alert: AppAlert = {
      id: this.alertIdCounter++,
      type,
      message,
      action,
    };

    const updated = [...this.alertsSubject.getValue(), alert];
    this.alertsSubject.next(updated);
    this.updateStorage(updated);
  }

  closeAlert(id: number) {
    this.hiddenAlertIds.add(id); // Temporarily hide
    const updated = this.alertsSubject.getValue().filter((a) => a.id !== id);
    this.alertsSubject.next(updated);
    // DO NOT touch localStorage
  }

  closeAlertByAction(actionType: string) {
    const updated = this.alertsSubject
      .getValue()
      .filter((alert) => alert.action !== actionType);
    this.alertsSubject.next(updated);
    localStorage.setItem('app_alerts', JSON.stringify(updated)); // if you're persisting
  }

  clearAllAlerts() {
    this.alertsSubject.next([]);
    localStorage.removeItem(this.alertsKey);
  }
  getBaseUrl(): string {
    return window.location.origin;
  }

  setPlanType(plan: string) {
    this.planTypeSubject.next(plan as 'none' | 'basic' | 'advanced');
  }

  getPlanType() {
    return this.planTypeSubject.value;
  }

  getPlanTypeObservable(): Observable<'none' | 'basic' | 'advanced'> {
    return this.planTypeSubject.asObservable();
  }

  // Shared LuQta logo loader for report/export PDFs (jsPDF needs a raster data URL, not the SVG logo).
  private readonly luqtaLogoPrimaryPath = 'assets/images/luqta_favicon1.png';
  private readonly luqtaLogoFallbackPath = 'assets/images/luqta_favicon2.png';
  private luqtaLogoDataUrl: string | null = null;

  async getLuqtaLogoDataUrl(): Promise<string | null> {
    if (this.luqtaLogoDataUrl) {
      return this.luqtaLogoDataUrl;
    }

    for (const path of [this.luqtaLogoPrimaryPath, this.luqtaLogoFallbackPath]) {
      try {
        const response = await fetch(path);
        if (!response.ok) {
          continue;
        }

        const blob = await response.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('Failed to read logo file'));
          reader.readAsDataURL(blob);
        });

        if (dataUrl) {
          this.luqtaLogoDataUrl = dataUrl;
          return dataUrl;
        }
      } catch {
        // Try next logo path.
      }
    }

    return null;
  }

  // Client's own profile photo (stored at login), for use as a stand-in banner preview in
  // exported reports when a contest has no uploaded banner. Falls back to the LuQta logo
  // when the client hasn't set a custom profile photo.
  private clientLogoDataUrl: string | null = null;

  async getClientLogoDataUrl(): Promise<string | null> {
    if (this.clientLogoDataUrl) {
      return this.clientLogoDataUrl;
    }

    const storedImage = localStorage.getItem('profileImage');
    if (storedImage && storedImage !== 'assets/images/profile.png') {
      const dataUrl = await this.getImageDataUrl(storedImage);
      if (dataUrl) {
        this.clientLogoDataUrl = dataUrl;
        return dataUrl;
      }
    }

    // Normalize to JPEG via the same canvas pipeline as getImageDataUrl, since preview-column
    // callers always addImage() this result as 'JPEG'.
    const luqtaLogo = await this.getLuqtaLogoDataUrl();
    const normalized = luqtaLogo ? await this.getImageDataUrl(luqtaLogo) : null;
    this.clientLogoDataUrl = normalized;
    return normalized;
  }

  // Some API responses double-prefix banner/prize image URLs with our own Xano domain in
  // front of an already-absolute URL (e.g. a Together AI short link or another Xano vault
  // path), and the embedded scheme's ':' is sometimes dropped in the process — e.g.
  // "https://xi5f-5y62-kbyr.n7e.xano.iohttps://api.together.ai/..." or
  // "...iohttps//api.together.ai/...". Strip the bogus prefix and restore the embedded URL.
  cleanImageUrl(url: string): string {
    if (!url) return '';
    const match = url.match(/^https:\/\/xi5f-5y62-kbyr\.n7e\.xano\.io(https?):?\/\/(.+)$/);
    return match ? `${match[1]}://${match[2]}` : url;
  }

  // Generic remote-image → data URL, for embedding contest banners (cross-origin) into exported PDFs.
  // Uses an <img crossOrigin="anonymous"> + canvas draw (not fetch()) since banner URLs are a
  // different origin than the app and may not send fetch-friendly CORS headers.
  getImageDataUrl(rawUrl: string): Promise<string | null> {
    return new Promise((resolve) => {
      const url = this.cleanImageUrl(rawUrl);
      if (!url) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          // Canvas tainted by CORS, or another draw failure — skip this image.
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
}
